import type React from 'react'
import { useRef } from 'react'
import { CancelIcon, SearchIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import {
	type BaseFormElementProps,
	type FormElementStyleProps,
	FormElementWrapper,
	formStyles,
	useFormElementState,
} from './FormElementWrapper'

interface SearchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'disabled'>, BaseFormElementProps {
	onSearch?: (value: string) => void
	clearable?: boolean
}

const searchStyles = {
	searchIcon: cva('fill-neutral-500', {
		variants: {
			variant: {
				default: '',
				error: '',
				filled: 'fill-neutral-800',
			},
			disabled: {
				true: 'fill-neutral-300',
				false: '',
			},
			hasValue: {
				true: 'fill-neutral-800',
				false: '',
			},
		},
		defaultVariants: {
			variant: 'default',
			disabled: false,
			hasValue: false,
		},
	}),
}

export const Search: React.FC<SearchProps> = ({
	label,
	hint,
	showHintIcon = false,
	variant = 'default',
	disabled = false,
	className,
	value: controlledValue,
	defaultValue = '',
	onChange,
	onSearch,
	clearable = true,
	placeholder = 'Search',
	onBlur,
	...props
}) => {
	const inputRef = useRef<HTMLInputElement>(null)

	const {
		value,
		handleChange,
		handleFocus,
		handleBlur: wrapperBlur,
		setInternalValue,
		styleProps,
	} = useFormElementState<HTMLInputElement>({
		value: controlledValue,
		defaultValue,
		variant,
		disabled,
		onChange,
	})

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		wrapperBlur()
		onBlur?.(e)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && onSearch) {
			onSearch(value.toString())
		}
	}

	const handleClear = () => {
		if (disabled) return

		setInternalValue('')
		if (inputRef.current) {
			inputRef.current.value = ''
			const event = new Event('change', { bubbles: true })
			inputRef.current.dispatchEvent(event)
		}

		const syntheticEvent = {
			target: { value: '' },
		} as React.ChangeEvent<HTMLInputElement>

		onChange?.(syntheticEvent)

		if (onSearch) {
			onSearch('')
		}

		inputRef.current?.focus()
	}

	const renderSearch = (inputStyleProps?: FormElementStyleProps) => {
		const mergedStyleProps = inputStyleProps || styleProps

		return (
			<div className={cn(formStyles.element(mergedStyleProps), 'relative flex items-center', className)}>
				<SearchIcon size={20} className={searchStyles.searchIcon(mergedStyleProps)} />

				<input
					ref={inputRef}
					className='w-full pl-2 pr-3 text-base bg-transparent focus:outline-none placeholder:text-neutral-500 text-neutral-800'
					disabled={disabled}
					value={value}
					onChange={handleChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					{...props}
				/>

				{clearable && value && !disabled && (
					<button
						onClick={handleClear}
						type='button'
						className={cn('cursor-pointer', mergedStyleProps.isFocused && '-mr-[2px]')}>
						<CancelIcon size={20} className='fill-neutral-800' />
					</button>
				)}
			</div>
		)
	}

	return (
		<FormElementWrapper
			label={label}
			hint={hint}
			showHintIcon={showHintIcon}
			variant={variant}
			disabled={disabled}
			isRequired={false}
			styleProps={styleProps}>
			{renderSearch()}
		</FormElementWrapper>
	)
}

export default Search
